import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface FullscreenControls {
  readonly active: boolean;
  /** False where the browser refuses to go full screen. */
  readonly supported: boolean;
  readonly enter: () => void;
  readonly exit: () => void;
}

/** The browser's own full screen for the element `ref` is attached to. */
export const useFullscreen = <T extends HTMLElement>(): [RefObject<T | null>, FullscreenControls] => {
  const ref = useRef<T>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onChange = () => setActive(ref.current !== null && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const enter = useCallback(() => {
    ref.current?.requestFullscreen().catch(() => undefined);
  }, []);
  const exit = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
  }, []);

  return [ref, { active, supported: Boolean(document.fullscreenEnabled), enter, exit }];
};
